import {
  BILLING_INTERVALS,
  type BillingInterval,
  type PlanDisplay,
  type PlanPrice,
} from "@giftforward/entitlements";
import { PLAN_INTERVAL_STRINGS } from "@/constants/Plans";
import { DECIMAL_2 } from "./number-format";

/**
 * Formatting and selection for catalogue prices (`PlanPrice`, amounts in cents), shared by the
 * upgrade drawer, the onboarding payment step and the plan summary bar.
 */

function currencyFormat(price: PlanPrice, options: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: price.currency.toUpperCase(),
    ...options,
  }).format(price.amount / 100);
}

/** "$1,000", "$95" — a headline price: whole dollars unless there are cents. */
export function formatPlanPrice(price: PlanPrice): string {
  return price.amount % 100 === 0
    ? currencyFormat(price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : currencyFormat(price, DECIMAL_2);
}

/** "$1,000.00" — what is charged: the order lines, the total and the Pay button. */
export function formatPlanCharge(price: PlanPrice): string {
  return currencyFormat(price, DECIMAL_2);
}

/**
 * The offer checkout charges: the chosen interval when the plan is sold at it, else the plan's
 * first offer (annual first, as GET /plans orders them). Null when the plan has no offer at all.
 */
export function selectedOffer(
  offers: readonly PlanPrice[],
  interval: BillingInterval,
): PlanPrice | null {
  return offers.find((o) => o.interval === interval) ?? offers[0] ?? null;
}

/** The intervals a plan is sold at, in pill order: Monthly, then Annual. */
export function offeredIntervals(offers: readonly PlanPrice[]): BillingInterval[] {
  return BILLING_INTERVALS.filter((i) => offers.some((o) => o.interval === i));
}

/** What the annual offer saves over twelve monthly payments; null when it saves nothing. */
export function annualSaving(offers: readonly PlanPrice[]): PlanPrice | null {
  const month = offers.find((o) => o.interval === "month");
  const year = offers.find((o) => o.interval === "year");
  if (!month || !year || month.currency !== year.currency) return null;
  const amount = month.amount * 12 - year.amount;
  return amount > 0 ? { interval: "year", amount, currency: year.currency } : null;
}

/** What the upgrade drawer shows for one plan at one pill. */
export interface CheckoutView {
  /** The offer Pay buys; null when the plan has none on sale (or the catalogue is loading). */
  offer: PlanPrice | null;
  /** Monthly / Annual pills: only when the plan is sold at more than one interval. */
  showPills: boolean;
  /** "$1,000.00" — order line, total, Pay button. */
  charge: string | null;
  /** The plan block: "$1,000" + "/year" + "Billed annually — save $140 a year". */
  amount: string;
  unit: string;
  billing: string;
}

/**
 * The drawer's prices for a plan and the selected pill. With a choice (Pro) the plan block shows
 * the selected offer; a plan sold one way (Pro Plus) keeps its card copy, since that copy already
 * describes the only offer.
 */
export function checkoutView(
  offers: readonly PlanPrice[],
  interval: BillingInterval,
  display: Pick<PlanDisplay, "amountLabel" | "unit" | "billingLabel"> | undefined,
): CheckoutView {
  const offer = selectedOffer(offers, interval);
  const showPills = offeredIntervals(offers).length > 1;
  const saving = annualSaving(offers);
  const block =
    showPills && offer
      ? {
          amount: formatPlanPrice(offer),
          unit: PLAN_INTERVAL_STRINGS.unit[offer.interval],
          billing: PLAN_INTERVAL_STRINGS.billingLine(
            offer.interval,
            saving ? formatPlanPrice(saving) : null,
          ),
        }
      : {
          amount: display?.amountLabel ?? "",
          unit: display?.unit ?? "",
          billing: display?.billingLabel ?? "",
        };
  return { offer, showPills, charge: offer ? formatPlanCharge(offer) : null, ...block };
}
